import React, { useState } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { useComposedRefs, useControllableState } from 'radix-ui/internal';

import {
  DEFAULT_DEBOUNCE_MS,
  DEFAULT_MIN_INPUT_QUERY_LENGTH,
  useGetAddressAutocompleteQuery,
} from '@/api/hooks/queries/use-get-address-autocomplete-query';
import type { SmartyAutocompleteProSuggestion } from '@/api/smarty-api.schema';
import { SensitiveField } from '@/components/SensitiveField';
import { CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

import { Input } from './ui/input';

interface AddressAutocompleteInputProps extends React.ComponentProps<
  typeof CommandPrimitive.Input
> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (address: string) => void;
  onValueSelect?: (address: SmartyAutocompleteProSuggestion) => void;
  placeholder?: string;
  limit?: number;
  debounceMs?: number;
  minInputQueryLength?: number;
}

export const AddressAutocompleteInput = ({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  onValueSelect,
  onBlur,
  placeholder = 'Search for an address...',
  limit = 5,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minInputQueryLength = DEFAULT_MIN_INPUT_QUERY_LENGTH,
  ref,
  ...props
}: AddressAutocompleteInputProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [value, setValue] = useControllableState({
    prop: controlledValue ?? '',
    defaultProp: defaultValue,
    onChange: onValueChange,
  });

  const [open, setOpen] = useState(false);

  const { data: suggestions } = useGetAddressAutocompleteQuery(
    {
      search: value,
      max_results: limit,
      source: 'postal',
      prefer_geolocation: 'city',
    },
    {
      enabled: open,
      debounceMs,
      minInputQueryLength,
    }
  );

  const handleInputChange = (newValue: string) => {
    // Defensive check: ensure newValue is a string (browser autofill can pass unexpected values)
    const safeValue = newValue ?? '';
    setValue(safeValue);
    setOpen(safeValue.length >= minInputQueryLength);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    if (event.key === 'Escape') {
      input.blur();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setOpen(false);
    onBlur?.(event);
  };

  const handleSelect = (address: SmartyAutocompleteProSuggestion) => {
    const streetLine = address?.street_line ?? '';
    const secondary = address?.secondary ?? '';
    setValue(`${streetLine} ${secondary}`.trim());
    onValueSelect?.(address);

    // Prevents the input from being focused after the user selects an option
    setTimeout(() => {
      inputRef?.current?.blur();
    }, 0);
  };

  return (
    <CommandPrimitive
      loop
      shouldFilter={false}
      value={value}
      data-slot="command"
      onKeyDown={handleKeyDown}
    >
      <div data-slot="command-input-wrapper">
        <CommandPrimitive.Input
          data-slot="command-input"
          placeholder={placeholder}
          value={value}
          onBlur={handleBlur}
          onValueChange={handleInputChange}
          asChild
          ref={useComposedRefs(ref, inputRef)}
          {...props}
        >
          <Input autoComplete="address-line1" />
        </CommandPrimitive.Input>
      </div>

      <div className="relative">
        <div
          className={cn(
            'absolute top-1 z-11 w-full animate-in rounded-xl bg-white fade-in-0 outline-none zoom-in-95',
            open && suggestions && suggestions.length > 0 ? 'block' : 'hidden'
          )}
        >
          <CommandList className="rounded-lg ring-1 ring-charcoal-100/30">
            {suggestions?.map((suggestion) => {
              const formattedAddress = `${suggestion.street_line} ${suggestion.secondary} ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`;

              return (
                <CommandItem
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  key={formattedAddress}
                  value={formattedAddress}
                  onSelect={() => handleSelect(suggestion)}
                >
                  <SensitiveField>
                    {suggestion.street_line} {suggestion.secondary}{' '}
                    <span className="text-xs text-charcoal-500 italic">
                      {suggestion.entries > 1 ? `(${suggestion.entries} results)` : ''}
                    </span>{' '}
                    {suggestion.city}, {suggestion.state} {suggestion.zipcode}{' '}
                  </SensitiveField>
                </CommandItem>
              );
            })}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
